import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";

import { Button } from "../ui/button";
import CaptureSheet from "../capture/CaptureSheet";
import { captureSourceFrom, sharedTextFrom } from "../../lib/captureSheet";

/**
 * PWA share-target and "Quick capture" shortcut landing. Anything shared into
 * ClaryNext lands here; Done hands it to the smart-capture sheet (spec
 * 2026-11-02 §3), including "Dive deeper?". Closing returns to Today.
 */
const CapturePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const shared = useMemo(() => sharedTextFrom(params), [params]);
  const source = useMemo(() => captureSourceFrom(params), [params]);

  const [text, setText] = useState(shared.slice(0, 5000));
  const [sheetText, setSheetText] = useState<string | null>(null);
  const canCapture = text.trim().length >= 3;

  return (
    <div className="flex-1 flex items-start justify-center pt-10 px-4">
      <div className="w-full max-w-md rounded-3xl border border-primary/15 bg-card p-6 shadow-sm">
        <h1 className="text-lg font-semibold text-foreground mb-1">{t("quick_capture")}</h1>
        {sheetText === null ? (
          <>
            <p className="text-sm text-muted-foreground mb-4">{t("capture_page_subtitle")}</p>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t("whats_on_your_mind")}
              rows={5}
              maxLength={5000}
              autoFocus
              dir="auto"
              className="w-full rounded-xl border border-gray-200 p-3 text-sm leading-relaxed text-start focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
            />
            <div className="flex justify-end mt-4">
              <Button disabled={!canCapture} onClick={() => setSheetText(text.trim())} className="rounded-full px-5 gap-1.5">
                <Sparkles className="w-4 h-4" />
                {t("capture.done")}
              </Button>
            </div>
          </>
        ) : (
          <div className="mt-3">
            <CaptureSheet text={sheetText} source={source} onClose={() => navigate("/home", { replace: true })} />
          </div>
        )}
      </div>
    </div>
  );
};

export default CapturePage;
